/**
 * AdminDecisionEnginePage — TurfPulse Decision Engine v1 control panel.
 *
 * Three sections:
 *   1. Engine version + L1 thresholds reference table
 *   2. Per-zone zone_type override editor (dry/avg/wet)
 *   3. Live preview sandbox — drives /api/decision-engine/evaluate
 *
 * Founder-only (admin email allowlist enforced on backend write endpoints).
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, Brain, Droplet, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { API_URL } from '../config/api';
import { SEO } from '../components/SEO';

const withAuth = () => ({ withCredentials: true });

const STATUS_COLOR = {
  GOOD:  'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
  WATCH: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
  DRY:   'bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300',
};

const REC_ICON = {
  WATER:   <Droplet size={14} className="text-rose-600" />,
  MONITOR: <AlertTriangle size={14} className="text-amber-600" />,
  SKIP:    <CheckCircle2 size={14} className="text-emerald-600" />,
};

const AdminDecisionEnginePage = () => {
  const navigate = useNavigate();
  const [versionInfo, setVersionInfo] = useState(null);
  const [overrides, setOverrides] = useState({});
  const [loading, setLoading] = useState(true);
  const [newZoneLabel, setNewZoneLabel] = useState('');
  const [newZoneType, setNewZoneType] = useState('avg');
  const [saving, setSaving] = useState(false);

  // Sandbox state — drives the live preview
  const [sandbox, setSandbox] = useState({
    moisture: 49,
    moisture_prev_24h: 51,
    rain_in_24h: 0,
    days_since_water: 3,
    ec_us_cm: '',
    ec_prev_24h: '',
    zone_type: 'avg',
  });
  const [previewDecision, setPreviewDecision] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/decision-engine/thresholds`, withAuth());
      setVersionInfo({
        engine_version: r.data.engine_version,
        default_zone_type: r.data.default_zone_type,
        thresholds: r.data.thresholds,
      });
      setOverrides(r.data.overrides || {});
    } catch (e) {
      console.error('Threshold load failed:', e?.response?.status, e?.message);
      toast.error('Could not load engine config');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const saveOverride = async () => {
    const label = newZoneLabel.trim();
    if (!label) {
      toast.error('Enter a zone label first');
      return;
    }
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/decision-engine/thresholds/${encodeURIComponent(label)}`,
        { zone_type: newZoneType },
        withAuth()
      );
      toast.success(`${label} → ${newZoneType}`);
      setNewZoneLabel('');
      await load();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Save failed';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const deleteOverride = async (label) => {
    try {
      await axios.delete(
        `${API_URL}/api/decision-engine/thresholds/${encodeURIComponent(label)}`,
        withAuth()
      );
      toast.success(`${label} reverted to default (avg)`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const runPreview = async () => {
    setPreviewLoading(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(sandbox)
          .map(([k, v]) => [k, v === '' || v === null ? null : (typeof v === 'string' ? Number(v) : v)])
          .filter(([k, v]) => k === 'zone_type' || v === null || !Number.isNaN(v))
      );
      payload.zone_type = sandbox.zone_type || null;
      const r = await axios.post(
        `${API_URL}/api/decision-engine/evaluate`,
        payload,
        withAuth()
      );
      setPreviewDecision(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Preview failed');
    } finally {
      setPreviewLoading(false);
    }
  };

  // Auto-run preview when sandbox changes (debounced via timeout)
  useEffect(() => {
    const t = setTimeout(runPreview, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(sandbox)]);

  const overrideRows = useMemo(() =>
    Object.entries(overrides).sort(([a], [b]) => a.localeCompare(b))
  , [overrides]);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950" data-testid="decision-engine-admin-page">
      <SEO title="Decision Engine v1 · Admin · TurfPulse" description="Tune TurfPulse's per-zone irrigation decision rules." />

      {/* Header */}
      <div className="max-w-5xl mx-auto px-4 py-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4"
          data-testid="back-button"
        >
          <ArrowLeft size={14} /> Back
        </button>
        <div className="flex items-center gap-3 mb-1">
          <Brain size={24} className="text-emerald-600 dark:text-emerald-400" />
          <h1 className="text-2xl font-bold">Decision Engine</h1>
          {versionInfo && (
            <Badge variant="outline" className="text-[10px] font-mono">v{versionInfo.engine_version}</Badge>
          )}
        </div>
        <p className="text-sm text-muted-foreground">12-rule cascade · zone-type aware · drives Zone Vitals + Rachio auto-fire skip gate.</p>
      </div>

      {/* L1 Thresholds reference */}
      {versionInfo && (
        <section className="max-w-5xl mx-auto px-4 mb-6">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Layer 1 — Zone-Type Thresholds</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="thresholds-table">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-wider text-muted-foreground border-b">
                      <th className="text-left py-2">Zone Type</th>
                      <th className="text-right py-2">WATER below</th>
                      <th className="text-right py-2">GOOD above</th>
                      <th className="text-left py-2 pl-4">Use for</th>
                    </tr>
                  </thead>
                  <tbody>
                    {['dry', 'avg', 'wet'].map(t => (
                      <tr key={t} className="border-b last:border-0">
                        <td className="py-2 font-mono uppercase">
                          {t}
                          {t === versionInfo.default_zone_type && <Badge className="ml-2 text-[9px]" variant="secondary">default</Badge>}
                        </td>
                        <td className="text-right py-2 font-mono">{versionInfo.thresholds[t].water_below}%</td>
                        <td className="text-right py-2 font-mono">{versionInfo.thresholds[t].good_above}%</td>
                        <td className="py-2 pl-4 text-xs text-muted-foreground">
                          {t === 'dry' && 'Western / full-sun / sandy zones'}
                          {t === 'avg' && 'Default — average lawn zones'}
                          {t === 'wet' && 'Shaded / north-facing / clay zones'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </section>
      )}

      {/* Per-zone overrides */}
      <section className="max-w-5xl mx-auto px-4 mb-6">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Per-Zone Overrides</CardTitle>
            <p className="text-xs text-muted-foreground">Tag a zone label as <span className="font-mono">dry</span>, <span className="font-mono">avg</span>, or <span className="font-mono">wet</span>. Untagged zones default to <span className="font-mono">avg</span>.</p>
          </CardHeader>
          <CardContent>
            {/* Add new */}
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_180px_auto] gap-3 mb-4">
              <div>
                <Label htmlFor="zone-label" className="text-xs">Zone Label</Label>
                <Input
                  id="zone-label"
                  data-testid="new-zone-label-input"
                  placeholder='Exact label e.g., "Zone 2" or "Zone 4/5"'
                  value={newZoneLabel}
                  onChange={(e) => setNewZoneLabel(e.target.value)}
                />
              </div>
              <div>
                <Label className="text-xs">Zone Type</Label>
                <Select value={newZoneType} onValueChange={setNewZoneType}>
                  <SelectTrigger data-testid="new-zone-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="dry">dry — west/sun/sandy</SelectItem>
                    <SelectItem value="avg">avg — default</SelectItem>
                    <SelectItem value="wet">wet — shade/clay</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={saveOverride} disabled={saving} className="self-end" data-testid="save-override-btn">
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </div>

            {/* Existing */}
            {loading && <p className="text-sm text-muted-foreground">Loading…</p>}
            {!loading && overrideRows.length === 0 && (
              <p className="text-sm text-muted-foreground italic" data-testid="no-overrides">No overrides — every zone uses the default <span className="font-mono">avg</span> thresholds.</p>
            )}
            {!loading && overrideRows.length > 0 && (
              <div className="border rounded">
                <table className="w-full text-sm" data-testid="overrides-table">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-wider text-muted-foreground border-b bg-muted/30">
                      <th className="text-left p-2">Zone Label</th>
                      <th className="text-left p-2">Type</th>
                      <th className="text-left p-2">Thresholds</th>
                      <th className="text-right p-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {overrideRows.map(([label, cfg]) => {
                      const t = versionInfo?.thresholds?.[cfg.zone_type];
                      return (
                        <tr key={label} className="border-b last:border-0" data-testid={`override-row-${label}`}>
                          <td className="p-2 font-semibold">{label}</td>
                          <td className="p-2 font-mono uppercase">{cfg.zone_type}</td>
                          <td className="p-2 text-xs text-muted-foreground font-mono">
                            {t ? `< ${t.water_below}% → WATER · > ${t.good_above}% → SKIP` : '—'}
                          </td>
                          <td className="p-2 text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteOverride(label)}
                              data-testid={`delete-override-${label}`}
                            >
                              Revert
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </section>

      {/* Live preview sandbox */}
      <section className="max-w-5xl mx-auto px-4 mb-12">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Live Preview Sandbox</CardTitle>
            <p className="text-xs text-muted-foreground">Tweak any input — the engine returns the same decision it would on a real zone.</p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Inputs */}
              <div className="space-y-3" data-testid="sandbox-inputs">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Moisture (%)</Label>
                    <Input
                      type="number"
                      value={sandbox.moisture}
                      onChange={(e) => setSandbox(s => ({ ...s, moisture: e.target.value === '' ? '' : Number(e.target.value) }))}
                      data-testid="sandbox-moisture"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Moisture 24h prior (%)</Label>
                    <Input
                      type="number"
                      value={sandbox.moisture_prev_24h}
                      onChange={(e) => setSandbox(s => ({ ...s, moisture_prev_24h: e.target.value === '' ? '' : Number(e.target.value) }))}
                      data-testid="sandbox-moisture-prev"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Rain in 24h (in)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={sandbox.rain_in_24h}
                      onChange={(e) => setSandbox(s => ({ ...s, rain_in_24h: e.target.value === '' ? '' : Number(e.target.value) }))}
                      data-testid="sandbox-rain"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Days since last water</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={sandbox.days_since_water}
                      onChange={(e) => setSandbox(s => ({ ...s, days_since_water: e.target.value === '' ? '' : Number(e.target.value) }))}
                      data-testid="sandbox-dsw"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">EC current (µS/cm)</Label>
                    <Input
                      type="number"
                      placeholder="optional"
                      value={sandbox.ec_us_cm}
                      onChange={(e) => setSandbox(s => ({ ...s, ec_us_cm: e.target.value === '' ? '' : Number(e.target.value) }))}
                      data-testid="sandbox-ec"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">EC 24h prior (µS/cm)</Label>
                    <Input
                      type="number"
                      placeholder="optional"
                      value={sandbox.ec_prev_24h}
                      onChange={(e) => setSandbox(s => ({ ...s, ec_prev_24h: e.target.value === '' ? '' : Number(e.target.value) }))}
                      data-testid="sandbox-ec-prev"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Zone Type</Label>
                  <Select
                    value={sandbox.zone_type}
                    onValueChange={(v) => setSandbox(s => ({ ...s, zone_type: v }))}
                  >
                    <SelectTrigger data-testid="sandbox-zone-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="dry">dry</SelectItem>
                      <SelectItem value="avg">avg (default)</SelectItem>
                      <SelectItem value="wet">wet</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Output */}
              <div data-testid="sandbox-output">
                {previewLoading && <p className="text-sm text-muted-foreground">Evaluating…</p>}
                {previewDecision && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 flex-wrap">
                      <Badge className={`text-sm font-bold px-3 py-1 ${STATUS_COLOR[previewDecision.status] || ''}`} data-testid="preview-status">
                        {previewDecision.status}
                      </Badge>
                      <div className="flex items-center gap-1 text-sm font-bold" data-testid="preview-recommendation">
                        {REC_ICON[previewDecision.recommendation]}
                        {previewDecision.recommendation}
                      </div>
                      {previewDecision.runtime_multiplier && previewDecision.runtime_multiplier !== 1.0 && (
                        <Badge variant="outline" className="text-xs">
                          runtime ×{previewDecision.runtime_multiplier.toFixed(2)}
                        </Badge>
                      )}
                    </div>

                    <div className="text-sm bg-muted/40 p-3 rounded border-l-2 border-emerald-500" data-testid="preview-reason">
                      <span className="font-semibold">Reason: </span>{previewDecision.reason}
                    </div>

                    {previewDecision.rules_fired?.length > 0 && (
                      <div className="text-xs">
                        <span className="text-muted-foreground">Rules fired: </span>
                        {previewDecision.rules_fired.map(r => (
                          <Badge key={r} variant="secondary" className="font-mono mr-1" data-testid={`rule-fired-${r}`}>
                            R{r}
                          </Badge>
                        ))}
                      </div>
                    )}

                    {previewDecision.alerts?.length > 0 && (
                      <div className="space-y-1">
                        {previewDecision.alerts.map((a, i) => (
                          <div key={i} className="text-xs p-2 rounded border-l-2 border-amber-400 bg-amber-50/40 dark:bg-amber-950/20" data-testid={`preview-alert-${a.code}`}>
                            <span className="font-bold uppercase mr-2">{a.severity}</span>
                            {a.text}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </section>
    </div>
  );
};

export default AdminDecisionEnginePage;
